import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PromptVersion, VersionStats } from '../types/api';
import { versionApi } from '../services/versionApi';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import * as Diff from 'diff';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptId: number;
  promptTitle: string;
  onVersionRestore?: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  promptId,
  promptTitle,
  onVersionRestore
}) => {
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [stats, setStats] = useState<VersionStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<PromptVersion | null>(null);
  const [compareVersion, setCompareVersion] = useState<PromptVersion | null>(null);
  const [showCreateVersionModal, setShowCreateVersionModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState<any>(null);

  // 使用useCallback包装loadVersions函数，避免无限循环
  const loadVersions = useCallback(async () => {
    if (!promptId) return;
    
    setIsLoading(true);
    try {
      const response = await versionApi.getVersions(promptId);
      
      if (response.success && response.data) {
        setVersions(response.data.versions);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('加载版本历史失败:', error);
      toast.error('加载版本历史失败');
    } finally {
      setIsLoading(false);
    }
  }, [promptId]); // 只依赖promptId

  // 恢复到指定版本
  const handleRevertToVersion = async (version: PromptVersion) => {
    const confirmed = window.confirm(
      `确定要恢复到版本 ${version.version_number} 吗？\n\n这将创建一个新版本，内容为版本 ${version.version_number} 的内容。`
    );
    
    if (!confirmed) return;

    try {
      const description = `恢复到版本 ${version.version_number}: ${version.change_description || '无描述'}`;
      await versionApi.revertToVersion(promptId, version.version_number, {
        changeDescription: description
      });
      
      toast.success('版本恢复成功');
      onVersionRestore?.();
      await loadVersions(); // 重新加载版本列表
    } catch (error) {
      console.error('版本恢复失败:', error);
      toast.error('版本恢复失败');
    }
  };

  // 删除版本
  const handleDeleteVersion = async (version: PromptVersion) => {
    if (version.version_number === stats?.latest_version) {
      toast.error('不能删除最新版本');
      return;
    }

    const confirmed = window.confirm(
      `确定要删除版本 ${version.version_number} 吗？\n\n此操作不可撤销。`
    );
    
    if (!confirmed) return;

    try {
      await versionApi.deleteVersion(promptId, version.version_number);
      toast.success('版本删除成功');
      await loadVersions(); // 重新加载版本列表
    } catch (error) {
      console.error('版本删除失败:', error);
      toast.error('版本删除失败');
    }
  };

  // 创建新版本
  const handleCreateVersion = async (description: string) => {
    try {
      await versionApi.createVersion(promptId, { changeDescription: description });
      toast.success('版本记录创建成功');
      setShowCreateVersionModal(false);
      await loadVersions(); // 重新加载版本列表
    } catch (error) {
      console.error('创建版本记录失败:', error);
      toast.error('创建版本记录失败');
    }
  };

  // 比较两个版本
  const handleCompareVersions = async (version1: PromptVersion, version2: PromptVersion) => {
    try {
      const response = await versionApi.compareVersions(promptId, version1.version_number, version2.version_number);
      setCompareData({
        version1,
        version2,
        comparison: response.comparison
      });
      setShowCompareModal(true);
    } catch (error) {
      console.error('版本比较失败:', error);
      toast.error('版本比较失败');
    }
  };

  // 处理版本比较按钮点击
  const handleCompareClick = (version: PromptVersion) => {
    if (!compareVersion) {
      // 第一次选择，设置为比较版本
      setCompareVersion(version);
      toast(`已选择版本 ${version.version_number}，请选择另一个版本进行比较`);
    } else if (compareVersion.id === version.id) {
      // 取消选择
      setCompareVersion(null);
      toast('已取消版本比较选择');
    } else {
      // 第二次选择，开始比较
      handleCompareVersions(compareVersion, version);
      setCompareVersion(null); // 比较后清除选择
    }
  };

  useEffect(() => {
    if (isOpen && promptId) {
      loadVersions();
    }
  }, [isOpen, promptId, loadVersions]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        >
          {/* 头部 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                版本历史
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1 truncate">
                {promptTitle}
              </p>
            </div>
            
            {/* 统计信息 */}
            {stats && (
              <div className="flex items-center space-x-4 mr-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{stats.total_versions}</div>
                  <div className="text-xs text-gray-500">版本数</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.contributors}</div>
                  <div className="text-xs text-gray-500">贡献者</div>
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {/* 操作按钮 */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowCreateVersionModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <i className="fas fa-save mr-2"></i>
                  保存当前版本
                </button>
                
                <button
                  onClick={loadVersions}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <i className={`fas fa-sync-alt mr-2 ${isLoading ? 'animate-spin' : ''}`}></i>
                  刷新
                </button>
              </div>

              {compareVersion && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    已选择版本 {compareVersion.version_number} 进行比较
                  </span>
                  <button
                    onClick={() => setCompareVersion(null)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 版本列表 */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">加载版本历史...</p>
                </div>
              </div>
            ) : versions.length > 0 ? (
              <div className="space-y-4">
                {versions.map((version) => (
                  <motion.div
                    key={version.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-gray-50 dark:bg-gray-700 rounded-xl p-4 border-2 transition-all ${
                      selectedVersion?.id === version.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg'
                        : compareVersion?.id === version.id
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            version.version_number === stats?.latest_version
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            <i className="fas fa-tag mr-1"></i>
                            版本 {version.version_number}
                            {version.version_number === stats?.latest_version && ' (最新)'}
                          </span>
                          
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(version.created_at)}
                          </span>
                          
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            by {version.created_by_name || '用户'}
                          </span>
                        </div>

                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                          {version.title}
                        </h4>
                        
                        {version.change_description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {version.change_description}
                          </p>
                        )}

                        <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3">
                          {version.content.substring(0, 200)}
                          {version.content.length > 200 && '...'}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => setSelectedVersion(selectedVersion?.id === version.id ? null : version)}
                          className={`p-2 transition-colors ${
                            selectedVersion?.id === version.id
                              ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/50 rounded-md'
                              : 'text-gray-400 hover:text-blue-600'
                          }`}
                          title={selectedVersion?.id === version.id ? '关闭详情' : '查看详情'}
                        >
                          <i className={`fas ${selectedVersion?.id === version.id ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                        
                        <button
                          onClick={() => handleCompareClick(version)}
                          className={`p-2 transition-colors ${
                            compareVersion?.id === version.id
                              ? 'text-orange-600'
                              : 'text-gray-400 hover:text-orange-600'
                          }`}
                          title={compareVersion ? (compareVersion.id === version.id ? '取消比较' : '比较版本') : '选择比较'}
                        >
                          <i className="fas fa-balance-scale"></i>
                        </button>
                        
                        {version.version_number !== stats?.latest_version && (
                          <button
                            onClick={() => handleRevertToVersion(version)}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                            title="恢复到此版本"
                          >
                            <i className="fas fa-undo"></i>
                          </button>
                        )}
                        
                        {version.version_number !== stats?.latest_version && (
                          <button
                            onClick={() => handleDeleteVersion(version)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="删除版本"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="fas fa-history text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  暂无版本历史
                </h3>
                <p className="text-gray-500 dark:text-gray-500">
                  开始编辑提示词后将自动创建版本记录
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* 创建版本模态框 */}
        {showCreateVersionModal && (
          <CreateVersionModal
            isOpen={showCreateVersionModal}
            onClose={() => setShowCreateVersionModal(false)}
            onSubmit={handleCreateVersion}
          />
        )}

        {/* 版本详情模态框 */}
        {selectedVersion && (
          <VersionDetailModal
            isOpen={!!selectedVersion}
            onClose={() => setSelectedVersion(null)}
            version={selectedVersion}
          />
        )}

        {/* 版本比较模态框 */}
        {showCompareModal && compareData && (
          <VersionCompareModal
            isOpen={showCompareModal}
            onClose={() => {
              setShowCompareModal(false);
              setCompareData(null);
            }}
            compareData={compareData}
          />
        )}
      </div>
    </AnimatePresence>
  );
};

// 创建版本模态框组件
interface CreateVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (description: string) => void;
}

const CreateVersionModal: React.FC<CreateVersionModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(description);
    setDescription('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            保存版本记录
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                版本描述 (可选)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="描述本次修改的内容..."
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                取消
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                保存版本
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// 差异高亮组件
interface DiffViewProps {
  oldText: string;
  newText: string;
  title1: string;
  title2: string;
}

const DiffView: React.FC<DiffViewProps> = ({ oldText, newText, title1, title2 }) => {
  const diff = Diff.diffWords(oldText, newText);
  
  const renderDiffText = (parts: Diff.Change[], isOldVersion: boolean) => {
    return parts.map((part, index) => {
      if (isOldVersion) {
        // 旧版本：显示删除的内容（红色背景）和保持不变的内容
        if (part.removed) {
          return (
            <span key={index} className="bg-red-200 dark:bg-red-900/50 text-red-800 dark:text-red-300 px-1 rounded">
              {part.value}
            </span>
          );
        } else if (!part.added) {
          return <span key={index}>{part.value}</span>;
        }
        return null;
      } else {
        // 新版本：显示添加的内容（绿色背景）和保持不变的内容
        if (part.added) {
          return (
            <span key={index} className="bg-green-200 dark:bg-green-900/50 text-green-800 dark:text-green-300 px-1 rounded">
              {part.value}
            </span>
          );
        } else if (!part.removed) {
          return <span key={index}>{part.value}</span>;
        }
        return null;
      }
    });
  };

  return (
    <div className="grid grid-cols-2 h-full">
      {/* 旧版本（左侧） */}
      <div className="p-6 border-r border-gray-200 dark:border-gray-700">
        <div className="h-full overflow-y-auto">
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2 flex items-center">
              <i className="fas fa-minus-circle mr-2"></i>
              {title1}
            </h4>
            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
              {renderDiffText(diff, true)}
            </div>
          </div>
        </div>
      </div>

      {/* 新版本（右侧） */}
      <div className="p-6">
        <div className="h-full overflow-y-auto">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2 flex items-center">
              <i className="fas fa-plus-circle mr-2"></i>
              {title2}
            </h4>
            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
              {renderDiffText(diff, false)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 版本详情模态框组件
interface VersionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  version: PromptVersion;
}

const VersionDetailModal: React.FC<VersionDetailModalProps> = ({
  isOpen,
  onClose,
  version
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              版本详情
            </h2>
            <div className="flex items-center space-x-3 mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                <i className="fas fa-tag mr-1"></i>
                版本 {version.version_number}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(version.created_at)}
              </span>
                             <span className="text-sm text-gray-500 dark:text-gray-400">
                 by {version.created_by_name || '用户'}
               </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

                 {/* 内容 */}
         <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 200px)' }}>
           <div className="space-y-6">
            {/* 基本信息 */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                基本信息
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    标题
                  </label>
                                     <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                     {version.title}
                   </div>
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     分类
                   </label>
                   <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                     {version.category || '未分类'}
                   </div>
                 </div>
                 <div className="md:col-span-2">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     变更描述
                   </label>
                   <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                     {version.change_description || '无描述'}
                   </div>
                 </div>
                {version.tags && version.tags.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      标签
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {version.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

                         {/* 内容 */}
             <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
               <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                 内容
               </h3>
               <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                 <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono leading-relaxed">
                   {version.content}
                 </pre>
               </div>
             </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              字符数: {version.content.length}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(version.content);
                  toast.success('内容已复制到剪贴板');
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i className="fas fa-copy mr-2"></i>
                复制内容
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// 版本比较模态框组件
interface VersionCompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  compareData: {
    version1: PromptVersion;
    version2: PromptVersion;
    comparison: any;
  };
}

const VersionCompareModal: React.FC<VersionCompareModalProps> = ({
  isOpen,
  onClose,
  compareData
}) => {
  if (!isOpen) return null;

  const { version1, version2 } = compareData;
  
  // 根据版本号确定新旧版本，版本号大的是新版本
  const oldVersion = version1.version_number > version2.version_number ? version2 : version1;
  const newVersion = version1.version_number > version2.version_number ? version1 : version2;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
      >
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            版本比较
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* 版本信息对比 */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="grid grid-cols-2 gap-6">
            <div className="text-center">
              <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-3 py-1 rounded-full text-sm font-medium inline-block mb-2">
                版本 {oldVersion.version_number} (旧版本)
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{oldVersion.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(oldVersion.created_at)} • {oldVersion.created_by_name || '用户'}
              </p>
              {oldVersion.change_description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {oldVersion.change_description}
                </p>
              )}
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium inline-block mb-2">
                版本 {newVersion.version_number} (新版本)
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{newVersion.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(newVersion.created_at)} • {newVersion.created_by_name || '用户'}
              </p>
              {newVersion.change_description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {newVersion.change_description}
                </p>
              )}
            </div>
          </div>
          
          {/* 标题差异对比 */}
          {oldVersion.title !== newVersion.title && (
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2 flex items-center">
                <i className="fas fa-edit mr-2"></i>
                标题变更
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-red-600 dark:text-red-400 font-medium">旧标题：</span>
                  <span className="bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 px-2 py-1 rounded">
                    {oldVersion.title}
                  </span>
                </div>
                <div>
                  <span className="text-green-600 dark:text-green-400 font-medium">新标题：</span>
                  <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 px-2 py-1 rounded">
                    {newVersion.title}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 内容对比 */}
        <div className="flex-1 overflow-hidden">
          <DiffView
            oldText={oldVersion.content}
            newText={newVersion.content}
            title1={`版本 ${oldVersion.version_number} 内容`}
            title2={`版本 ${newVersion.version_number} 内容`}
          />
        </div>

        {/* 底部操作 */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              关闭
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default VersionHistoryModal; 